import type { CalendarEvent } from '../types.js';
import {
  eventDurationMinutes,
  isDateOnlyISO,
  localHourFraction,
} from './dateUtils.js';

export interface PositionedEvent {
  readonly event: CalendarEvent;
  /** Start hour-fraction in viewer TZ (e.g. 9.5 for 9:30). */
  readonly startHour: number;
  /** End hour-fraction in viewer TZ (clamped at 24). */
  readonly endHour: number;
  /** Column this event occupies in the day's overlap-resolved layout. */
  readonly column: number;
  /** Total number of columns for this event's overlap cluster. */
  readonly columns: number;
}

const MIN_BLOCK_MINUTES = 30;

/**
 * Lay out a day's timed events on a column-grid. Returns events with
 * `{column, columns}` so the renderer can compute `left = column /
 * columns` and `width = 1 / columns` for each block.
 *
 * Algorithm: scan events ordered by start time. For each event, pick the
 * earliest column whose last event ended before this one starts; the
 * cluster's column count is the running max. When a gap (no overlap with
 * any in-flight event) appears, reset the cluster.
 *
 * All-day events are excluded (caller renders them separately via
 * `AllDayStrip`).
 */
export function layoutDayEvents(
  events: readonly CalendarEvent[],
  viewerTz?: string,
  defaultDurationMinutes = 60,
): readonly PositionedEvent[] {
  // 1. Project to hour fractions, drop all-day, drop invalid.
  const items: { event: CalendarEvent; startHour: number; endHour: number }[] = [];
  for (const event of events) {
    if (event.allDay === true || isDateOnlyISO(event.startsAt)) continue;
    const startHour = localHourFraction(event.startsAt, viewerTz);
    if (startHour === null) continue;
    const durMin = eventDurationMinutes(event.startsAt, event.endsAt) ?? defaultDurationMinutes;
    const minDur = Math.max(durMin, MIN_BLOCK_MINUTES);
    const endHour = Math.min(24, startHour + minDur / 60);
    items.push({ event, startHour, endHour });
  }
  items.sort((a, b) => a.startHour - b.startHour || a.endHour - b.endHour);

  // 2. Cluster into overlap groups; assign columns within each cluster.
  const out: PositionedEvent[] = [];
  let cluster: { event: CalendarEvent; startHour: number; endHour: number; column: number }[] = [];
  let clusterEnd = -Infinity;

  const flushCluster = (): void => {
    if (cluster.length === 0) return;
    const columns = cluster.reduce((m, it) => Math.max(m, it.column + 1), 0);
    for (const it of cluster) {
      out.push({
        event: it.event,
        startHour: it.startHour,
        endHour: it.endHour,
        column: it.column,
        columns,
      });
    }
    cluster = [];
    clusterEnd = -Infinity;
  };

  for (const it of items) {
    if (it.startHour >= clusterEnd) flushCluster();
    // Find the earliest available column.
    const occupied = new Set<number>();
    for (const c of cluster) {
      if (c.endHour > it.startHour) occupied.add(c.column);
    }
    let col = 0;
    while (occupied.has(col)) col++;
    cluster.push({ ...it, column: col });
    clusterEnd = Math.max(clusterEnd, it.endHour);
  }
  flushCluster();

  return out;
}
